import React, { useState } from "react";
import { Container } from "react-bootstrap";
import CodeBlock from "./CodeBlock";

const secoes = [
  { id: "s1", num: "01", label: "O que são Subagents?" },
  { id: "s2", num: "02", label: "Criando um Subagent" },
  { id: "s3", num: "03", label: "Projetando Eficazmente" },
  { id: "s4", num: "04", label: "Usando Eficazmente" },
];

const antipadroes = [
  {
    id: "ap1",
    titulo: "Reivindicações de especialidade",
    corpo: `Subagents que alegam especialização raramente ajudam. Prompts como "você é um especialista em Python" ou "você é um especialista em Kubernetes" não agregam valor — o Claude já possui esse conhecimento. Não há nada que um suposto subagent especialista possa fazer que seu thread principal não consiga fazer diretamente.`,
  },
  {
    id: "ap2",
    titulo: "Pipelines sequenciais",
    corpo: `Pipelines sequenciais de subagents criam problemas. Considere um fluxo de três agentes: um para reproduzir um bug, um para depurá-lo e um para corrigi-lo. Pipelines funcionam quando as tarefas são verdadeiramente independentes. Eles falham quando cada etapa depende das descobertas da etapa anterior — e a correção de bugs quase sempre depende. Informações se perdem na transferência entre agentes.`,
  },
  {
    id: "ap3",
    titulo: "Executores de testes",
    corpo: `Subagents executores de testes tendem a esconder informações que você precisa. Quando testes falham, você quer a saída completa para diagnosticar os problemas. Um subagent que retorna apenas "testes falharam" te força a criar scripts de debug adicionais para obter detalhes que estariam visíveis na saída direta. Testes mostraram que este padrão teve o pior desempenho entre todas as configurações.`,
  },
];

function GuiaSubagents() {
  const [secaoAtiva, setSecaoAtiva] = useState("s1");
  const [openAp, setOpenAp] = useState(new Set());

  const toggleAp = (id) => {
    setOpenAp((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const irPara = (id) => {
    setSecaoAtiva(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const idxAtivo = secoes.findIndex((s) => s.id === secaoAtiva);
  const progresso = ((idxAtivo + 1) / secoes.length) * 100;

  return (
    <Container className="guia-sub mb-5 transicao" data-bs-theme="dark">
      <h1>SUBAGENTS — CLAUDE CODE</h1>
      <hr />
      <p className="text-muted" style={{ fontFamily: "var(--sub-mono)", fontSize: "13px" }}>
        Baseado no material da Anthropic · Guia interativo
      </p>

      <div className="guia-sub-progress">
        <div className="guia-sub-progress-fill" style={{ width: `${progresso}%` }} />
      </div>

      <div className="guia-sub-section-nav">
        {secoes.map((s) => (
          <button
            key={s.id}
            className={`guia-sub-section-btn${secaoAtiva === s.id ? " active" : ""}`}
            onClick={() => irPara(s.id)}
          >
            <span className="guia-sub-section-num">{s.num}</span>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Seção 1: O que são Subagents? ── */}
      {secaoAtiva === "s1" && (
        <div>
          <div className="guia-sub-topic-tag">Tópico 01</div>
          <h2 className="guia-sub-h2">O que são Subagents?</h2>

          <p>
            Subagents são <strong>assistentes especializados</strong> para os quais o Claude Code pode delegar tarefas.
            Pense neles como ajudantes focados: cada um roda em sua própria janela de contexto de conversa, executa
            seu trabalho e retorna um resumo para o thread principal. As etapas intermediárias — todas as leituras de
            arquivos, buscas e chamadas de ferramentas — ficam isoladas e nunca poluem a sua conversa principal.
          </p>

          <h3 className="guia-sub-h3">Por que Subagents são importantes?</h3>
          <p>
            Toda vez que você conversa com o Claude Code, você está adicionando conteúdo à{" "}
            <strong>janela de contexto principal</strong>. Cada chamada de ferramenta, cada arquivo lido, cada
            resultado de busca é armazenado ali. Esse espaço é finito — e quando se esgota, o Claude começa a perder
            o fio das partes mais antigas da conversa.
          </p>

          <div className="guia-sub-blockquote">
            <strong>Os subagents resolvem esse problema</strong> abrindo uma janela de contexto separada.
          </div>

          <p>O subagent recebe duas coisas:</p>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>O que recebe</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td><strong>System prompt personalizado</strong></td>
                  <td>Do seu arquivo de configuração — define o papel e o comportamento do subagent</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td><strong>Descrição da tarefa</strong></td>
                  <td>Escrita pelo agente pai com base no que você pediu</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            O subagent então trabalha por conta própria: lê arquivos, faz buscas, edita código — o que for
            necessário. Quando termina, <strong>apenas um resumo volta</strong> para a sua conversa principal. A
            conversa inteira do subagent é então descartada.
          </p>

          <div className="guia-sub-blockquote">
            <strong>Trade-off:</strong> você perde a visibilidade de como o subagent chegou às suas conclusões.
          </div>

          <h3 className="guia-sub-h3">Exemplo prático</h3>
          <p>Imagine que você está explorando uma base de código desconhecida e quer saber qual serviço cuida de reembolsos.</p>

          <div className="guia-sub-compare">
            <div className="guia-sub-compare-box bad">
              <h4>Sem um subagent</h4>
              <ul className="list-unstyled m-0">
                <li>O Claude leria ~15 arquivos</li>
                <li>Executaria várias buscas</li>
                <li>Rastrearia múltiplas chamadas de função</li>
                <li>Tudo isso ocuparia espaço no contexto</li>
              </ul>
            </div>
            <div className="guia-sub-compare-box good">
              <h4>Com um subagent</h4>
              <ul className="list-unstyled m-0">
                <li>Você faz a pergunta</li>
                <li>O subagent <code>Explore</code> investiga no seu próprio contexto</li>
                <li>Ele retorna uma resposta objetiva</li>
                <li>Seu contexto registra apenas a pergunta e o resumo</li>
              </ul>
            </div>
          </div>

          <h3 className="guia-sub-h3">Subagents nativos</h3>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Subagent</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>General Purpose</strong></td>
                  <td>Para tarefas de múltiplos passos que exigem exploração e ação</td>
                </tr>
                <tr>
                  <td><strong>Explore</strong></td>
                  <td>Para buscas rápidas e navegação em bases de código</td>
                </tr>
                <tr>
                  <td><strong>Plan</strong></td>
                  <td>Usado no modo de planejamento para pesquisa e análise antes de apresentar um plano</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="guia-sub-h3">Subagents personalizados</h3>
          <p>
            Além das opções nativas, você pode <strong>criar seus próprios subagents</strong> com system prompts e
            acesso a ferramentas customizados. Exemplos:
          </p>
          <div className="guia-sub-card-grid">
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🔍</div>
              <h4>Revisor de código</h4>
              <p>Analisa mudanças com critérios específicos do projeto</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🧪</div>
              <h4>Escritor de testes</h4>
              <p>Gera testes unitários e de integração automaticamente</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">📝</div>
              <h4>Gerador de docs</h4>
              <p>Documenta funções, classes e módulos do projeto</p>
            </div>
          </div>

          <h3 className="guia-sub-h3">Pontos-chave</h3>
          <div className="guia-sub-card-grid">
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🎯</div>
              <h4>Foco</h4>
              <p>Dividem o trabalho em partes específicas, cada um se concentra em uma tarefa</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🧹</div>
              <h4>Contexto limpo</h4>
              <p>Isolam todo o trabalho intermediário, mantendo a conversa principal organizada</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">📦</div>
              <h4>Respostas objetivas</h4>
              <p>Trazem apenas a informação necessária em forma de resumo conciso</p>
            </div>
          </div>

          <div className="guia-sub-nav-btns">
            <button className="guia-sub-btn primary" onClick={() => irPara("s2")}>
              Próximo: Criando um Subagent →
            </button>
          </div>
        </div>
      )}

      {/* ── Seção 2: Criando um Subagent ── */}
      {secaoAtiva === "s2" && (
        <div>
          <div className="guia-sub-topic-tag">Tópico 02</div>
          <h2 className="guia-sub-h2">Criando um Subagent</h2>

          <p>
            O Claude Code já vem com subagents nativos, mas você também pode <strong>criar os seus próprios</strong>.
            Subagents personalizados se especializam em tarefas específicas — como revisar código, escrever testes ou
            verificar documentação. Eles são definidos como{" "}
            <strong>arquivos markdown com frontmatter YAML</strong> que dizem ao Claude quando usar o subagent e como
            ele deve se comportar.
          </p>

          <h3 className="guia-sub-h3">Como criar um subagent</h3>
          <p>
            A forma mais fácil de criar um subagent é pelo comando <code>/agents</code>. Ele abre a interface
            principal de gerenciamento de subagents. A partir daí, selecione <strong>"Create new agent"</strong>.
          </p>
          <p>
            Primeiro, você escolhe o <strong>escopo</strong> do subagent:
          </p>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Escopo</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Project-level</strong></td>
                  <td>Disponível apenas no projeto atual</td>
                </tr>
                <tr>
                  <td><strong>User-level</strong></td>
                  <td>Compartilhado entre todos os projetos da sua máquina</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="guia-sub-blockquote">
            A <strong>abordagem recomendada é deixar o Claude gerar a configuração por você</strong>. Basta descrever
            o que você quer que o subagent faça, e o Claude produzirá um nome, uma descrição e um system prompt
            automaticamente.
          </div>

          <h3 className="guia-sub-h3">Personalizando as ferramentas</h3>
          <p>Durante a criação, você pode personalizar quais ferramentas o subagent pode acessar:</p>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Read-only tools</strong></td>
                  <td>Leitura de arquivos e busca</td>
                </tr>
                <tr>
                  <td><strong>Edit tools</strong></td>
                  <td>Edição de arquivos</td>
                </tr>
                <tr>
                  <td><strong>Execution tools</strong></td>
                  <td>Execução de comandos</td>
                </tr>
                <tr>
                  <td><strong>MCP tools</strong></td>
                  <td>Integrações via MCP</td>
                </tr>
                <tr>
                  <td><strong>Other tools</strong></td>
                  <td>Demais ferramentas</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="guia-sub-blockquote">
            <strong>Pense no que o seu subagent realmente precisa.</strong> Um revisor de código provavelmente não
            precisa de ferramentas de edição — ele deve ler e analisar código, não alterá-lo.
          </div>

          <h3 className="guia-sub-h3">Escolhendo o modelo e a cor</h3>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th>Indicado para</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Haiku</strong></td>
                  <td>Tarefas rápidas e leves</td>
                </tr>
                <tr>
                  <td><strong>Sonnet</strong></td>
                  <td>Equilíbrio entre velocidade e profundidade</td>
                </tr>
                <tr>
                  <td><strong>Opus</strong></td>
                  <td>Análises complexas</td>
                </tr>
                <tr>
                  <td><strong>Inherit</strong></td>
                  <td>Usa o mesmo modelo da conversa principal</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Por fim, você escolhe uma <strong>cor</strong> — ela aparece na interface para que você identifique
            rapidamente qual subagent está ativo.
          </p>

          <h3 className="guia-sub-h3">O arquivo de configuração</h3>
          <p>
            Depois da criação, o arquivo é salvo tipicamente em{" "}
            <code>.claude/agents/nome-do-agente.md</code>:
          </p>
          <CodeBlock code={`---
name: code-quality-reviewer
description: Use this agent when you need to review recently written
  or modified code for quality, security, and best practice compliance.
tools: Bash, Glob, Grep, Read, WebFetch, WebSearch
model: sonnet
color: purple
---

You are an expert code reviewer specializing in quality assurance,
security best practices, and adherence to project standards.`} />

          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>name</code></td>
                  <td>Identificador único. Use com <code>@agent nome-do-agente</code> para referenciá-lo</td>
                </tr>
                <tr>
                  <td><code>description</code></td>
                  <td>
                    Controla quando o Claude decide usar o subagent.{" "}
                    <strong>Deve ser uma única linha</strong>
                  </td>
                </tr>
                <tr>
                  <td><code>tools</code></td>
                  <td>Lista de ferramentas que o subagent pode acessar — editável a qualquer momento</td>
                </tr>
                <tr>
                  <td><code>model</code></td>
                  <td>
                    Modelo Claude a usar: <code>sonnet</code>, <code>opus</code>, <code>haiku</code> ou{" "}
                    <code>inherit</code>
                  </td>
                </tr>
                <tr>
                  <td><code>color</code></td>
                  <td>Cor de identificação na interface</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="guia-sub-h3">System prompts</h3>
          <p>
            O corpo do arquivo markdown — <strong>tudo abaixo do frontmatter YAML</strong> — é o system prompt. É
            aqui que você dá as instruções ao subagent.
          </p>
          <div className="guia-sub-blockquote">
            <strong>
              Um system prompt bem escrito é a diferença entre um subagent útil e um que erra o ponto.
            </strong>{" "}
            Seja específico sobre o que o subagent deve procurar e como deve estruturar sua saída.
          </div>

          <h3 className="guia-sub-h3">Fazendo o Claude usar seu subagent automaticamente</h3>
          <p>
            Inclua a palavra <code>"proactively"</code> no campo <code>description</code> para que o Claude delegue
            automaticamente:
          </p>
          <CodeBlock code={`description: Proactively suggest running this agent after major code changes...`} />
          <p>
            Adicionar <strong>exemplos de conversa</strong> à descrição também ajuda o Claude a entender cenários
            específicos. Quanto mais concretos os exemplos, melhor.
          </p>

          <h3 className="guia-sub-h3">Testando seu subagent</h3>
          <p>Após criar o subagent, teste fazendo algumas alterações de código e pedindo ao Claude para revisá-las.</p>
          <div className="guia-sub-blockquote">
            <strong>Se o subagent não estiver sendo acionado quando esperado</strong>, revise o campo{" "}
            <code>description</code>. Adicionar exemplos mais específicos e cenários de ativação resolve esse problema.
          </div>

          <div className="guia-sub-nav-btns">
            <button className="guia-sub-btn" onClick={() => irPara("s1")}>
              ← Anterior
            </button>
            <button className="guia-sub-btn primary" onClick={() => irPara("s3")}>
              Próximo: Projetando Eficazmente →
            </button>
          </div>
        </div>
      )}

      {/* ── Seção 3: Projetando Subagents Eficazes ── */}
      {secaoAtiva === "s3" && (
        <div>
          <div className="guia-sub-topic-tag">Tópico 03</div>
          <h2 className="guia-sub-h2">Projetando Subagents Eficazes</h2>

          <p>
            Um subagent mal configurado vai divagar, rodar por tempo demais ou produzir uma saída que o agente
            principal não consegue aproveitar. As correções se resumem a{" "}
            <strong>quatro pilares</strong>: escrever boas descrições, definir um formato de saída, reportar
            obstáculos e limitar o acesso a ferramentas.
          </p>

          <h3 className="guia-sub-h3">Como os dados de configuração são usados</h3>
          <p>
            Quando você envia uma mensagem ao agente principal, o{" "}
            <strong>nome e a descrição de cada subagent disponível</strong> são incluídos no system prompt. É assim
            que o agente principal decide qual subagent iniciar e quando.
          </p>
          <div className="guia-sub-blockquote">
            <strong>
              A descrição não controla apenas quando um subagent roda — ela também molda o que o subagent recebe como
              instrução.
            </strong>
          </div>

          <h3 className="guia-sub-h3">Escrevendo descrições que moldam os prompts de entrada</h3>
          <p>
            Com uma descrição genérica, o agente principal pode gerar um prompt vago como{" "}
            <em>"use git diff para encontrar as mudanças atuais"</em>. Com uma descrição específica como{" "}
            <em>"Você deve dizer ao agente precisamente quais arquivos quer que ele revise"</em>, o prompt gerado
            listará os arquivos exatos.
          </p>
          <p>
            Essa técnica funciona para qualquer tipo de subagent. Por exemplo, adicionar{" "}
            <em>"retorne fontes que possam ser citadas"</em> à descrição de um subagent de busca na web faz com que o
            agente principal inclua essa instrução ao delegar.
          </p>

          <h3 className="guia-sub-h3">Definindo um formato de saída</h3>
          <p>
            <strong>
              A melhoria mais importante que você pode fazer em um subagent é definir um formato de saída no system
              prompt.
            </strong>{" "}
            Isso tem dois efeitos:
          </p>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Efeito</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td><strong>Cria pontos naturais de parada</strong></td>
                  <td>O subagent sabe que terminou quando preencheu cada seção</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td><strong>Evita execução excessiva</strong></td>
                  <td>Sem saída definida, subagents tendem a rodar muito mais do que o necessário</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>Exemplo de formato estruturado para um subagent de revisão de código:</p>
          <CodeBlock code={`Forneça sua revisão no seguinte formato estruturado:

1. Resumo: Visão geral do que foi revisado e avaliação geral
2. Problemas Críticos: Vulnerabilidades de segurança, riscos de integridade
   de dados ou erros de lógica que devem ser corrigidos imediatamente
3. Problemas Maiores: Problemas de qualidade, desalinhamento de arquitetura
   ou preocupações significativas de performance
4. Problemas Menores: Inconsistências de estilo, lacunas de documentação
   ou otimizações menores
5. Recomendações: Sugestões de melhoria, oportunidades de refatoração
   ou boas práticas a aplicar
6. Status de Aprovação: Declaração clara sobre se o código está pronto
   para merge/deploy ou requer alterações
7. Obstáculos Encontrados: Setup issues, workarounds descobertos,
   comandos que precisaram de flags especiais, dependências problemáticas`} />

          <h3 className="guia-sub-h3">Reportando obstáculos</h3>
          <p>
            Quando um subagent descobre uma solução alternativa durante seu trabalho,{" "}
            <strong>esses detalhes precisam aparecer no resumo que ele retorna</strong>. Se não aparecerem, o thread
            principal terá que redescobrir as mesmas soluções, desperdiçando tempo e tokens.
          </p>
          <p>Tipos de informação que devem ser surfaceados:</p>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Exemplos</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Setup e ambiente</strong></td>
                  <td>Problemas de setup ou peculiaridades do ambiente</td>
                </tr>
                <tr>
                  <td><strong>Soluções alternativas</strong></td>
                  <td>Workarounds descobertos durante a execução da tarefa</td>
                </tr>
                <tr>
                  <td><strong>Comandos especiais</strong></td>
                  <td>Comandos que precisaram de flags ou configurações não-padrão</td>
                </tr>
                <tr>
                  <td><strong>Dependências problemáticas</strong></td>
                  <td>Imports ou pacotes que causaram conflitos ou erros</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="guia-sub-blockquote">
            A forma de garantir essa informação é{" "}
            <strong>pedir explicitamente por ela no formato de saída</strong>. Adicionar uma seção "Obstáculos
            Encontrados" resolve isso de forma confiável.
          </div>

          <h3 className="guia-sub-h3">Limitando o acesso a ferramentas</h3>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Tipo de subagent</th>
                  <th>Ferramentas recomendadas</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Pesquisa / somente leitura</strong></td>
                  <td><code>Glob</code>, <code>Grep</code>, <code>Read</code></td>
                  <td>Não pode modificar arquivos acidentalmente</td>
                </tr>
                <tr>
                  <td><strong>Revisor de código</strong></td>
                  <td><code>Bash</code> (para <code>git diff</code>)</td>
                  <td>Precisa ver o que mudou, mas não editar</td>
                </tr>
                <tr>
                  <td><strong>Agente de modificação</strong></td>
                  <td><code>Edit</code>, <code>Write</code></td>
                  <td>O trabalho dele é alterar o código</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="guia-sub-h3">Os quatro pilares</h3>
          <div className="guia-sub-card-grid">
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🎯</div>
              <h4>Descrições específicas</h4>
              <p>Controla quando o subagent é iniciado e quais instruções ele recebe</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">📋</div>
              <h4>Saída estruturada</h4>
              <p>Define quando o subagent terminou e garante respostas utilizáveis</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🚧</div>
              <h4>Reporte de obstáculos</h4>
              <p>Evita que o thread principal redescubra soluções já encontradas</p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🔒</div>
              <h4>Acesso limitado</h4>
              <p>Previne efeitos colaterais e torna o papel de cada subagent mais claro</p>
            </div>
          </div>

          <div className="guia-sub-nav-btns">
            <button className="guia-sub-btn" onClick={() => irPara("s2")}>
              ← Anterior
            </button>
            <button className="guia-sub-btn primary" onClick={() => irPara("s4")}>
              Próximo: Usando Eficazmente →
            </button>
          </div>
        </div>
      )}

      {/* ── Seção 4: Usando Subagents Eficazmente ── */}
      {secaoAtiva === "s4" && (
        <div>
          <div className="guia-sub-topic-tag">Tópico 04</div>
          <h2 className="guia-sub-h2">Usando Subagents Eficazmente</h2>

          <p>
            Você já sabe como criar subagents e projetá-los bem. Agora a questão é: quando eles realmente ajudam —
            e quando atrapalham? A diferença se resume a uma coisa:{" "}
            <strong>se o trabalho intermediário importa para o seu thread principal</strong>.
          </p>

          <h3 className="guia-sub-h3">A regra de decisão</h3>
          <div className="guia-sub-decision">
            <div className="guia-sub-decision-question">O trabalho intermediário importa?</div>
            <div className="guia-sub-decision-row">
              <div className="guia-sub-decision-opt yes">
                <div className="label">Não importa → Delegue</div>
                <p>Você só precisa do resultado final. Use um subagent para manter o contexto principal limpo.</p>
              </div>
              <div className="guia-sub-decision-opt no">
                <div className="label">Importa → Thread principal</div>
                <p>
                  Você precisa ver e reagir ao que está acontecendo no caminho. Mantenha no thread principal.
                </p>
              </div>
            </div>
          </div>

          <h3 className="guia-sub-h3">Quando subagents se destacam</h3>
          <p>Subagents funcionam melhor quando a exploração está separada da execução. Eles se destacam em tarefas onde:</p>
          <div className="table-responsive">
            <table className="guia-sub-table">
              <thead>
                <tr>
                  <th>Condição</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Resultado final importa</strong></td>
                  <td>Você precisa de um resultado, não de um passo a passo de como ele foi encontrado</td>
                </tr>
                <tr>
                  <td><strong>Contexto em risco</strong></td>
                  <td>O trabalho exploratório poluiria o contexto do seu thread principal</td>
                </tr>
                <tr>
                  <td><strong>Perspectiva isolada</strong></td>
                  <td>A tarefa se beneficia de uma visão nova ou de um system prompt personalizado</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="guia-sub-h3">Casos de uso ideais</h3>

          <h4 className="guia-sub-h4">Pesquisa</h4>
          <p>
            Pesquisa é o caso de uso clássico. Um subagent de pesquisa pode ler dezenas de arquivos e rastrear
            chamadas de função. <strong>Toda essa exploração fica no contexto do subagent.</strong> Seu thread
            principal recebe apenas:
          </p>
          <CodeBlock code={`A validação do JWT acontece em middleware/auth.js linha 42,
chamada pelo Express router em route/api.js`} />

          <h4 className="guia-sub-h4">Revisões de código</h4>
          <p>
            O Claude revisa código de forma mais eficaz quando o código é apresentado como{" "}
            <strong>sendo de autoria de outra pessoa</strong>. Se você construiu uma feature ao longo de muitas
            interações, o mesmo thread tem dificuldade de enxergá-la com olhos frescos.
          </p>
          <div className="guia-sub-blockquote">
            Um subagent revisor vê as mudanças em um contexto separado — sem o histórico de como o código foi
            escrito. Isso também permite codificar{" "}
            <strong>padrões de revisão específicos do projeto</strong> no system prompt.
          </div>

          <h4 className="guia-sub-h4">System prompts personalizados</h4>
          <div className="guia-sub-card-grid">
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">✍️</div>
              <h4>Subagent de copywriting</h4>
              <p>
                Instruções sobre tom, público e estilo — ideal para landing pages e campanhas de e-mail, onde o
                padrão técnico do Claude Code não serve.
              </p>
            </div>
            <div className="guia-sub-card">
              <div className="guia-sub-card-icon">🎨</div>
              <h4>Subagent de estilização</h4>
              <p>
                Carrega os arquivos do seu design system no contexto. Já conhece variáveis de cor, espaçamento e
                padrões de componentes antes de escrever qualquer CSS.
              </p>
            </div>
          </div>

          <h3 className="guia-sub-h3">Quando subagents atrapalham</h3>
          <p>
            Existem <strong>três anti-padrões</strong> comuns a evitar. Clique para expandir cada um:
          </p>

          {antipadroes.map((ap) => (
            <div key={ap.id} className={`guia-sub-ap${openAp.has(ap.id) ? " open" : ""}`}>
              <div className="guia-sub-ap-header" onClick={() => toggleAp(ap.id)}>
                <div className="guia-sub-ap-icon">⚠️</div>
                <h4>{ap.titulo}</h4>
                <span className="guia-sub-ap-chevron">▼</span>
              </div>
              <div className="guia-sub-ap-body">{ap.corpo}</div>
            </div>
          ))}

          <h3 className="guia-sub-h3">Resumo final</h3>
          <div className="guia-sub-compare">
            <div className="guia-sub-compare-box good">
              <h4>Use subagents para</h4>
              <ul className="list-unstyled m-0">
                <li>Pesquisa e exploração</li>
                <li>Revisões de código</li>
                <li>Tarefas que precisam de um system prompt personalizado</li>
              </ul>
            </div>
            <div className="guia-sub-compare-box bad">
              <h4>Evite subagents para</h4>
              <ul className="list-unstyled m-0">
                <li>Personas de "especialista" sem valor real</li>
                <li>Pipelines onde cada passo depende do anterior</li>
                <li>Execução de testes onde você precisa da saída completa</li>
              </ul>
            </div>
          </div>

          <div className="guia-sub-nav-btns">
            <button className="guia-sub-btn" onClick={() => irPara("s3")}>
              ← Anterior
            </button>
            <button className="guia-sub-btn primary" onClick={() => irPara("s1")}>
              Recomeçar do início ↩
            </button>
          </div>
        </div>
      )}
    </Container>
  );
}

export default GuiaSubagents;
